import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { CartItem, Product, ColorVariant } from '../data/products';

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: { product: Product; selectedColor?: string; selectedColorVariant?: ColorVariant } }
  | { type: 'REMOVE_FROM_CART'; payload: { productId: string; selectedColor?: string } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; selectedColor?: string; quantity: number } }
  | { type: 'CLEAR_CART' };

const initialState: CartState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
};

const getInitialCartState = (): CartState => {
  if (typeof window === 'undefined') return initialState;

  try {
    const storedItems = JSON.parse(localStorage.getItem('c-nine-cart') || '[]');
    if (!Array.isArray(storedItems)) return initialState;

    return storedItems.reduce(
      (state: CartState, item: CartItem) => {
        if (!item?.product?.id || !item.quantity || item.quantity < 1) return state;
        const itemPrice = getItemPrice(item);
        return {
          items: [...state.items, item],
          totalItems: state.totalItems + item.quantity,
          totalPrice: state.totalPrice + itemPrice * item.quantity,
        };
      },
      initialState
    );
  } catch {
    localStorage.removeItem('c-nine-cart');
    return initialState;
  }
};

const getItemPrice = (item: { product: Product; selectedColorVariant?: ColorVariant }): number => {
  if (item.selectedColorVariant?.price !== undefined && item.selectedColorVariant.price > 0) {
    return item.selectedColorVariant.price;
  }
  return item.product.price;
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const { product, selectedColor, selectedColorVariant } = action.payload;
      const existingItem = state.items.find(
        item => item.product.id === product.id && item.selectedColor === selectedColor
      );
      
      // Don't add if product with same variant already exists (prevent duplicates)
      if (existingItem) {
        return state;
      }
      
      // Add new product variant
      const newItem: CartItem = { product, quantity: 1, selectedColor, selectedColorVariant };
      const itemPrice = getItemPrice(newItem);
      return {
        ...state,
        items: [...state.items, newItem],
        totalItems: state.totalItems + 1,
        totalPrice: state.totalPrice + itemPrice,
      };
    }
    
    case 'REMOVE_FROM_CART': {
      const { productId, selectedColor } = action.payload;
      const itemToRemove = state.items.find(
        item => item.product.id === productId && (selectedColor === undefined || item.selectedColor === selectedColor)
      );
      if (!itemToRemove) return state;
      
      const updatedItems = state.items.filter(
        item => !(item.product.id === productId && (selectedColor === undefined || item.selectedColor === selectedColor))
      );
      const itemPrice = getItemPrice(itemToRemove);
      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems - itemToRemove.quantity,
        totalPrice: state.totalPrice - (itemPrice * itemToRemove.quantity),
      };
    }
    
    case 'UPDATE_QUANTITY': {
      const { id, selectedColor, quantity } = action.payload;
      if (quantity <= 0) {
        return cartReducer(state, { type: 'REMOVE_FROM_CART', payload: { productId: id, selectedColor } });
      }
      
      const item = state.items.find(
        i => i.product.id === id && (selectedColor === undefined || i.selectedColor === selectedColor)
      );
      if (!item) return state;
      
      const updatedItems = state.items.map(i => {
        if (i.product.id === id && (selectedColor === undefined || i.selectedColor === selectedColor)) {
          return { ...i, quantity };
        }
        return i;
      });
      
      const quantityDiff = quantity - item.quantity;
      const itemPrice = getItemPrice(item);
      return {
        ...state,
        items: updatedItems,
        totalItems: state.totalItems + quantityDiff,
        totalPrice: state.totalPrice + (itemPrice * quantityDiff),
      };
    }
    
    case 'CLEAR_CART':
      return initialState;
    
    default:
      return state;
  }
};

interface CartContextType {
  state: CartState;
  addToCart: (product: Product, selectedColor?: string, selectedColorVariant?: ColorVariant) => boolean;
  removeFromCart: (productId: string, selectedColor?: string) => void;
  updateQuantity: (productId: string, quantity: number, selectedColor?: string) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, undefined, getInitialCartState);

  useEffect(() => {
    localStorage.setItem('c-nine-cart', JSON.stringify(state.items));
  }, [state.items]);

  const addToCart = (product: Product, selectedColor?: string, selectedColorVariant?: ColorVariant): boolean => {
    const existingItem = state.items.find(
      item => item.product.id === product.id && item.selectedColor === selectedColor
    );
    if (existingItem) {
      return false;
    }
    dispatch({ type: 'ADD_TO_CART', payload: { product, selectedColor, selectedColorVariant } });
    return true;
  };

  const removeFromCart = (productId: string, selectedColor?: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { productId, selectedColor } });
  };

  const updateQuantity = (productId: string, quantity: number, selectedColor?: string) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, selectedColor, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  return (
    <CartContext.Provider value={{ state, addToCart, removeFromCart, updateQuantity, clearCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
